import { Link } from "react-router-dom";
import { defineListView } from "@/base/list/defineListView";
import type { ListFilterSource } from "@/base/list/types";
import type { AuthedBlobRequester, AuthedRequester } from "@/lib/brokerAuth";
import { downloadAccountDocument } from "@/lib/brokerAccounts";
import type { BondSubmissionListItem } from "@/lib/brokerBonds";
import { submissionStatusLabel } from "@/lib/bondStateModel";
import {
  BOND_HISTORY_STATUS_OPTIONS,
  BOND_HISTORY_TYPE_OPTIONS,
  bondHistoryFigure,
  bondHistoryType,
  listBondHistory,
} from "@/lib/bondHistory";
import { formatBondCurrency } from "@/lib/bondValueDisplay";
import { formatInvoiceAmount } from "@/lib/invoices";
import { formatShortDate, statusBadgeClass } from "@/lib/listFormat";
import { BondFormDownload } from "@/components/surety-account/BondFormDownload";

// BRK-S406 — the Surety account's Bond history as a BaseListView definition,
// embedded in the profile (BRK-S255: every list is one) on both the Company
// profile and Bond history tabs (BRK-S501).
//
// THE WIREFRAME'S NINE COLUMNS (BRK-S501), a `wide` list: Submission ID (the
// date under it) · Obligee · Bond type · State · SFAA code · Total contract
// price · Status · Premium · Bond form. Cards below lg; from lg a table that
// scrolls inside its own frame.
//
// SERVER-SIDE (BRK-S435): the three filters, the paging and the totals row
// are PMS's — `year`, `bondType` and `status` go on the GET beside
// `accountId`, and the footer renders the served aggregates over the filtered
// set. The Year filter's options are the served `years`; Bond type is the
// fixed kind vocabulary (BRK-S436); Status the v2 vocabulary. PMS-S838,
// pinned at BRK-S511, so `listViewDefsContract` checks the definition.
//
// URL keys of its own (`bhYear`, `bhType`, `bhStatus`, `bhPage`): the profile
// holds its tab in `?tab=`, and Billing its list under `bill*`.

const DASH = "—";
const CHIP =
  "inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium";

/** The wire's parameter names (PMS-S838). */
const YEAR = "year";
const TYPE = "bondType";
const STATUS = "status";

/** The footnote the wireframe puts under the table. */
export const SFAA_FOOTNOTE =
  "SFAA codes stay blank until Sutton provides them.";

export function bondHistoryListView({
  request,
  fetchBlob,
  accountId,
}: {
  request: AuthedRequester;
  /** The authenticated file read, for the bond form downloads. */
  fetchBlob: AuthedBlobRequester;
  accountId: string;
}) {
  // The Year options are the served `years`, read from the unfiltered first
  // page — one row is enough to carry them.
  const years: ListFilterSource = {
    endpoint: "/api/broker/bond/submissions",
    load: async () =>
      (
        await listBondHistory(request, { accountId, limit: 1, offset: 0 })
      ).years.map((y) => ({ value: y, label: y })),
  };

  return defineListView<BondSubmissionListItem>({
    endpoint: "/api/broker/bond/submissions",
    noun: "bond submissions",
    title: "Bond history",
    wide: true,
    columns: [
      {
        id: "submission",
        header: "Submission ID",
        kind: "title",
        cell: (r) => <span className="tabular-nums">{r.number}</span>,
        subtitle: (r) => formatShortDate(r.createdAt),
      },
      {
        id: "obligee",
        header: "Obligee",
        cell: (r) => r.obligeeName ?? DASH,
      },
      {
        id: "type",
        header: "Bond type",
        cell: (r) => bondHistoryType(r.bondKinds) ?? DASH,
      },
      {
        id: "state",
        header: "State",
        cell: (r) => r.projectState ?? DASH,
      },
      {
        // A placeholder upstream: null until Sutton provides the codes, so it
        // is blank (the footnote says why), never invented.
        id: "sfaa",
        header: "SFAA code",
        cell: (r) => r.sfaaCode ?? "",
      },
      {
        id: "price",
        header: "Total contract price",
        align: "right",
        cell: (r) => money(bondHistoryFigure(r.contractPrice)),
      },
      {
        id: "status",
        header: "Status",
        cell: (r) => (
          <span className={`${CHIP} ${statusBadgeClass(String(r.status))}`}>
            {submissionStatusLabel(r.status)}
          </span>
        ),
      },
      {
        // D6: the ISSUED premium — the invoiced figure the broker already
        // sees on the invoice. PMS serves it only once a bond is invoiced.
        id: "premium",
        header: "Premium",
        align: "right",
        cell: (r) => premium(bondHistoryFigure(r.premium)),
      },
      {
        id: "form",
        header: "Bond form",
        cell: (r) => {
          const documentId = r.bondFormDocumentId;
          return documentId ? (
            <BondFormDownload
              number={r.number}
              download={() =>
                downloadAccountDocument(fetchBlob, accountId, documentId)
              }
            />
          ) : (
            <span className="text-xs text-brk-dim">Not issued yet</span>
          );
        },
      },
    ],
    rowKey: (r) => r.id,
    rowHref: (r) => `/surety/submissions/${encodeURIComponent(r.id)}`,
    rowLabel: (r) => r.number,
    filters: [
      {
        urlKey: "bhYear",
        apiParam: YEAR,
        label: "Year",
        allLabel: "All years",
        options: years,
      },
      {
        urlKey: "bhType",
        apiParam: TYPE,
        label: "Bond type",
        allLabel: "All bond types",
        options: BOND_HISTORY_TYPE_OPTIONS,
      },
      {
        urlKey: "bhStatus",
        apiParam: STATUS,
        label: "Status",
        allLabel: "All statuses",
        options: BOND_HISTORY_STATUS_OPTIONS,
      },
    ],
    pageKey: "bhPage",
    pageSize: 25,
    empty: {
      title: "No bond submissions for this insured yet.",
      action: (
        <Link
          to="/surety/construction"
          className="text-sm font-medium text-brk-accent hover:underline"
        >
          Start a bond request →
        </Link>
      ),
    },
    load: async ({ filters, limit, offset }) => {
      const page = await listBondHistory(request, {
        accountId,
        year: filters[YEAR],
        bondType: filters[TYPE],
        status: filters[STATUS],
        limit,
        offset,
      });
      const { totals } = page;
      return {
        items: page.items,
        total: page.total,
        limit: page.limit,
        offset: page.offset,
        // PMS's aggregates over the filtered set — never a sum of this page.
        totals: {
          submission: `${String(totals.count)} ${
            totals.count === 1 ? "submission" : "submissions"
          }`,
          price: money(totals.contractPrice),
          premium: premium(totals.premium),
        },
      };
    },
  });
}

/** A contract price for display (D-10), or a dash — never $0 — when absent. */
function money(value: number | null) {
  return (
    <span className="tabular-nums">
      {value === null ? DASH : formatBondCurrency(value)}
    </span>
  );
}

/** The premium keeps its cents (`formatInvoiceAmount`), or a dash. */
function premium(value: number | null) {
  return (
    <span className="tabular-nums">
      {value === null ? DASH : formatInvoiceAmount(value)}
    </span>
  );
}
